import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, Typography, IconButton, Button, Checkbox, List, ListItem, ListItemText, ListItemIcon, Divider, Switch, Snackbar, Alert, TextField, Card, CardContent, CircularProgress } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { EventInput, EventClickArg, EventApi, DateSelectArg, EventDropArg } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { database } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { ref, get, set, update, push } from 'firebase/database';
import firebaseDataService from '../../services/FirebaseDataService';
// NOT: onValue kullanımları kaldırıldı - offline mod için
import EventFormModal from './EventFormModal';
import CalendarEventModal from './CalendarEventModal';
import BulkEventModal from './BulkEventModal';
import CalendarEventDetailModal from './CalendarEventDetailModal';
import GuideButton from '../common/guide/GuideButton';
import Agenda from './Agenda';
import { useTranslation } from 'react-i18next';

// EventFormItem tipi tanımlanıyor (BulkEventModal'dan)
interface EventFormItem {
  title: string;
  start: Date;
  end: Date;
  specialEventId?: string; // İlişkilendirilmiş özel etkinliğin ID'si
  eventColor?: string; // Etkinliğin rengi
}

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  createdAt: number;
  isChecked?: boolean;
  color?: string; // Özel etkinlik rengi
}

interface EventItem extends EventInput {
  title: string;
  start: string | Date;
  end?: string | Date;
  allDay?: boolean;
  notes?: string;
  repeatYearly?: boolean;
  specialEventId?: string; // Özel etkinlik ID'si
  eventColor?: string; // Doğrudan etkinliğe atanmış renk (özel etkinlikten alınır)
}

// Ajanda notu türü tanımı
interface AgendaNote {
  id: string;
  date: string; // ISO formatında tarih
  content: string;
  createdAt: number;
  updatedAt: number;
}

// Mini takvim için günleri localize etme fonksiyonu
const getDayNames = (isTr: boolean) => (isTr ? ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'] : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']);

// Mini takvim için ayları localize etme fonksiyonu
const getMonthNames = (isTr: boolean) => (
  isTr
    ? ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
);

const Events: React.FC = () => {
  const { t, i18n } = useTranslation('other');
  const isTr = i18n.language?.startsWith('tr');
  const dateFnsLocale = isTr ? tr : enUS;
  // FullCalendar ref
  const calendarRef = useRef<any>(null);

  // Bugünün tarihini al
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  
  // Mini takvim için state
  const [currentDate, setCurrentDate] = useState(today);
  const [miniCalendarMonth, setMiniCalendarMonth] = useState(today);
  
  // Etkinlik listesi (boş başla)
  const [events, setEvents] = useState<EventItem[]>([]);

  // Etkinlik formu modal state'i
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Takvim etkinliği modal state'i
  const [isCalendarEventModalOpen, setIsCalendarEventModalOpen] = useState(false);
  // Toplu etkinlik modal state'i
  const [isBulkEventModalOpen, setIsBulkEventModalOpen] = useState(false);
  
  // Özel etkinlikleri tutacak state
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false);
  
  // Bildirim state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // Özel etkinlikler için detay modalı
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<CalendarEvent | null>(null);
  const [isCalendarEventDetailModalOpen, setIsCalendarEventDetailModalOpen] = useState(false);
  
  // Takvim görünümü state'i
  const [calendarView, setCalendarView] = useState<string>('dayGridMonth');
  
  // Günden yıla geçiş için tarih hatırlama state'i
  const [lastDayViewDate, setLastDayViewDate] = useState<Date | null>(null);
  
  // Ajanda görünümü state'leri
  const [showAgendaView, setShowAgendaView] = useState(false);
  const [agendaNotes, setAgendaNotes] = useState<AgendaNote[]>([]);
  const [selectedAgendaDate, setSelectedAgendaDate] = useState<Date>(new Date());
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [loadingAgendaNotes, setLoadingAgendaNotes] = useState(false);
  
  // Aktif özel etkinliklere göre takvim etkinliklerini filtrele
  const filterEventsByActiveCalendarEvents = useCallback((activeCalendarEvents: CalendarEvent[]) => {
    if (!calendarRef.current) return;
    
    // Boş özel etkinlik listesi varsa işlem yapma
    if (activeCalendarEvents.length === 0) return;
    
    try {
      const calendarApi = calendarRef.current.getApi();
      
      // Aktif/pasif özel etkinliklerin ID'lerini ve renklerini içeren nesne oluştur
      const specialEventStatus: {[key: string]: {active: boolean, color: string}} = {};
      
      // Döngü sayısını azaltmak için birleştirilmiş bir işlem yapıyoruz
      activeCalendarEvents.forEach(event => {
        specialEventStatus[event.id] = {
          active: event.isChecked || false,
          color: event.color || '#1976d2' // Varsayılan renk yoksa mavi kullan
        };
      });
      
      // Tüm takvim etkinliklerini al
      const allEvents = calendarApi.getEvents();
      
      // Filtre uygula - yalnızca ilgili etkinlikleri işle
      allEvents.forEach((event: EventApi) => {
        const specialEventId = event.extendedProps.specialEventId;
        
        // Sadece bir özel etkinliğe bağlı olanları kontrol et
        if (specialEventId && specialEventId in specialEventStatus) {
          const eventStatus = specialEventStatus[specialEventId];
          
          // Özel etkinlik aktifse göster, değilse gizle
          event.setProp('display', eventStatus.active ? 'auto' : 'none');
          
          // Renk ayarlamasını yap
          if (eventStatus.active) {
            event.setProp('backgroundColor', eventStatus.color);
            event.setProp('borderColor', eventStatus.color);
          }
        }
      });
    } catch (error) {
      console.error('Etkinlikler filtrelenirken hata:', error);
    }
  }, [calendarRef]);
  
  // Özel etkinliğin durumunu değiştirme
  const handleToggleCalendarEvent = useCallback(async (eventId: string) => {
    try {
      // İşlem başladığını belirt
      const targetEvent = calendarEvents.find(e => e.id === eventId);
      if (!targetEvent) return;
      
      // Yeni durumu belirle
      const newIsChecked = !targetEvent.isChecked;
      
      // Yerel state'i güncelle (hızlı UI tepkisi için)
      const updatedCalendarEvents = calendarEvents.map(event => 
        event.id === eventId 
          ? { ...event, isChecked: newIsChecked } 
          : event
      );
      
      // State'i güncellemek ve UI'yi yenilemek için
      setCalendarEvents(updatedCalendarEvents);
      
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Veritabanında güncelle
      const eventRef = ref(database, `companies/${companyId}/events/calendarEvents/${eventId}`);
      await set(eventRef, {
        ...targetEvent,
        isChecked: newIsChecked
      });
      
      // Filtrelemeleri uygula - timeout ile asenkron olarak çalıştır
      // Böylece UI bloke olmaz
      setTimeout(() => {
        filterEventsByActiveCalendarEvents(updatedCalendarEvents);
      }, 50);
      
    } catch (error) {
      console.error('Error while updating event status:', error);
    }
  }, [calendarEvents, filterEventsByActiveCalendarEvents]);

  const [selectedStart, setSelectedStart] = useState<Date | null>(null);
  const [selectedEnd, setSelectedEnd] = useState<Date | null>(null);
  const [selectedAllDay, setSelectedAllDay] = useState<boolean>(false);

  const handleDateClick = (arg: DateClickArg) => {
    const clickedDate = arg.date;
    // Eğer gün görünümünde değilsek, öğle saatini ayarla
    if (!arg.allDay) {
      // Zaten saat bilgisiyle tıklanmış
      setSelectedDate(clickedDate);
    } else {
      // Tarih tıklamasıysa, saat 12:00 olarak ayarla
      const dateWithNoon = new Date(clickedDate);
      dateWithNoon.setHours(12, 0, 0, 0);
      setSelectedDate(dateWithNoon);
    }
    setSelectedEvent(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = clickInfo.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start || new Date(),
      end: event.end || new Date(),
      allDay: event.allDay,
      notes: event.extendedProps.notes
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleSaveEvent = async (eventData: any) => {
    try {
      console.log('Gelen etkinlik veri:', eventData);
      
      // Modal'dan gelen ID'yi kontrol edelim
      // Eğer etkinlik ID'si varsa, bu EventFormModal tarafından kaydedilmiş demektir
      // Bu durumda veritabanına tekrar kaydetmeyiz, sadece UI'ı güncelleriz
      const modalSavedEvent = eventData._savedByModal === true;
      
      // Kullanıcı bilgilerini al
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        console.error('User login not found.');
        setSnackbarMessage(t('events.messages.userLoginNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        console.error('User information not found.');
        setSnackbarMessage(t('events.messages.userInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      const companyId = userData.companyId;
      
      if (!companyId) {
        console.error('Company information not found.');
        setSnackbarMessage(t('events.messages.companyInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      let eventId = eventData.id || '';
      
      // Düzenleme mi, yeni kayıt mı?
      if (isEditing && selectedEvent?.id) {
        // UI'ı güncelle - bunu her koşulda yapabiliriz
        const updatedEvents = events.map(event => 
          event.id === selectedEvent.id ? { 
            ...eventData, 
            id: selectedEvent.id,
            specialEventId: eventData.specialEventId,
            eventColor: eventData.eventColor // Renk bilgisini ekle
          } : event
        );
        setEvents(updatedEvents);
        
        // Tekrarlanan etkinlikler veya yerel etkinlikler DB'ye kaydedilmez
        // ayrıca modalSavedEvent true ise bunu Modal tarafında kaydedilmiş demektir
        if (!selectedEvent.id.includes('-recurring-') && !selectedEvent.id.startsWith('event-') && !modalSavedEvent) {
          // DB'ye kaydedilecek veriyi hazırla
          const eventDataForDB: any = {
            title: eventData.title,
            start: eventData.start.toISOString(),
            end: eventData.end.toISOString(),
            allDay: eventData.allDay,
            repeatYearly: eventData.repeatYearly,
            notes: eventData.notes || '',
            updatedAt: Date.now()
          };
          
          // Özel etkinlik seçildiyse ekle
          if (eventData.specialEventId) {
            eventDataForDB.specialEventId = eventData.specialEventId;
            
            // Özel etkinlik rengi varsa ekle
            if (eventData.eventColor) {
              eventDataForDB.eventColor = eventData.eventColor;
            }
          }
          
          // Mevcut etkinliği güncelle
          const eventRef = ref(database, `companies/${companyId}/events/${selectedEvent.id}`);
          await update(eventRef, eventDataForDB);
          console.log('Event updated:', selectedEvent.id);
        }
        
        // FullCalendar'ı güncelle
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          const eventInstance = calendarApi.getEventById(selectedEvent.id as string);
          if (eventInstance) {
            eventInstance.setProp('title', eventData.title);
            eventInstance.setStart(eventData.start);
            eventInstance.setEnd(eventData.end);
            eventInstance.setAllDay(eventData.allDay);
            eventInstance.setExtendedProp('notes', eventData.notes);
            eventInstance.setExtendedProp('repeatYearly', eventData.repeatYearly);
            eventInstance.setExtendedProp('specialEventId', eventData.specialEventId);
            eventInstance.setExtendedProp('eventColor', eventData.eventColor);
            
            // Renk değişikliğini uygula
            if (eventData.eventColor) {
              eventInstance.setProp('backgroundColor', eventData.eventColor);
              eventInstance.setProp('borderColor', eventData.eventColor);
            }
          }
        }
        
        // Başarılı mesajı göster
        setSnackbarMessage(t('events.messages.eventUpdated'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        // Yeni etkinlik oluşturma
        // Modal tarafından kaydedildiyse, tekrar DB'ye kaydetmeyiz
        if (!modalSavedEvent) {
          // DB'ye kaydedilecek veriyi hazırla
          const eventDataForDB: any = {
            title: eventData.title,
            start: eventData.start.toISOString(),
            end: eventData.end.toISOString(),
            allDay: eventData.allDay,
            repeatYearly: eventData.repeatYearly,
            notes: eventData.notes || '',
            updatedAt: Date.now(),
            createdAt: Date.now(),
            createdBy: user.uid
          };
          
          // Özel etkinlik seçildiyse ekle
          if (eventData.specialEventId) {
            eventDataForDB.specialEventId = eventData.specialEventId;
            
            // Özel etkinlik rengi varsa ekle
            if (eventData.eventColor) {
              eventDataForDB.eventColor = eventData.eventColor;
            }
          }
          
          // Yeni etkinlik oluştur
          const eventRef = ref(database, `companies/${companyId}/events`);
          const newEventRef = push(eventRef);
          eventId = newEventRef.key as string;
          
          if (!eventId) {
            console.error('Event ID could not be created.');
            setSnackbarMessage(t('events.messages.eventIdCreateFailed'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
          }
          
          // Oluşturma bilgilerini ekle
          eventDataForDB.id = eventId;
          
          // Veritabanına kaydet
          await set(newEventRef, eventDataForDB);
          console.log('New event created:', eventId);
        }
        
        // UI'a ekle
        const newEvent = {
          ...eventData,
          id: eventId || `event-${Date.now()}` // eğer ID yoksa, geçici bir ID oluştur
        };
        setEvents([...events, newEvent]);
        
        // Başarılı mesajı göster
        setSnackbarMessage(t('events.messages.eventCreated'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
      
      // Modal'ı kapat
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error while saving event:', error);
      setSnackbarMessage(t('events.messages.eventSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteEvent = async () => {
    try {
      if (!selectedEvent?.id) return;
      
      // Kullanıcı bilgilerini al
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId;
      
      if (!companyId) return;
      
      // Veritabanından sil (id "event-" ile başlıyorsa, yerel id'dir ve veritabanı kaydı yoktur)
      if (!selectedEvent.id.startsWith('event-') && !selectedEvent.id.includes('-recurring-')) {
        const eventRef = ref(database, `companies/${companyId}/events/${selectedEvent.id}`);
        await set(eventRef, null); // Firebase'de null değer atayarak kaydı siliyoruz
      }
      
      // Yerel state'i güncelle
      const updatedEvents = events.filter(event => event.id !== selectedEvent.id);
      setEvents(updatedEvents);
      
      // Takvimden de etkinliği kaldır
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const eventInstance = calendarApi.getEventById(selectedEvent.id as string);
        if (eventInstance) {
          eventInstance.remove();
        }
      }
      
      // Başarılı mesajı göster
      setSnackbarMessage(t('events.messages.eventDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Modal'ı kapat
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error while deleting event:', error);
      setSnackbarMessage(t('events.messages.eventDeleteError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Mini takvimde ay değiştirme
  const handlePrevMonth = () => {
    setMiniCalendarMonth(subMonths(miniCalendarMonth, 1));
  };

  const handleNextMonth = () => {
    setMiniCalendarMonth(addMonths(miniCalendarMonth, 1));
  };

  // Mini takvimde bugüne gitme
  const handleGoToToday = () => {
    setMiniCalendarMonth(today);
    setCurrentDate(today);
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
    }
  };

  // Tarih aralığı seçimi için event handler
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    // Seçilen başlangıç ve bitiş tarihlerini al
    const start = selectInfo.start;
    const end = selectInfo.end;
    const allDay = selectInfo.allDay;
    
    // Modal için veriyi hazırla
    setSelectedDate(null); // Tek tarih seçimini temizle
    setSelectedEvent(null); // Etkinlik seçimini temizle
    setIsEditing(false); // Yeni bir etkinlik oluşturacağız
    
    // Seçilen tarih aralığı bilgilerini state'e kaydet
    setSelectedStart(start);
    setSelectedEnd(end);
    setSelectedAllDay(allDay);
    
    // Modal'ı aç
    setIsModalOpen(true);
    
    // FullCalendar'ın seçim vurgusunu temizle
    const calendarApi = selectInfo.view.calendar;
    calendarApi.unselect();
  };

  // Mini takvimde gün seçme
  const handleMiniDateSelect = (date: Date) => {
    setCurrentDate(date);
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(date);
    }
  };

  // Mini takvim için gün dizisi oluşturma
  const getDaysArray = () => {
    const monthStart = startOfMonth(miniCalendarMonth);
    const monthEnd = endOfMonth(miniCalendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Hafta başlangıcını Pazartesi olarak ayarlamak için kaydırma yapalım
    let firstDayOfWeek = new Date(monthStart).getDay(); // 0=Pazar, 1=Pazartesi, ...
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Pazartesi=0, ... Pazar=6

    // Takvim hücreleri için boş alanları ekleyelim
    const prefixDays = Array(firstDayOfWeek).fill(null);
    
    return [...prefixDays, ...daysInMonth];
  };

  // Manuel olarak Türkçe çevirileri tanımlayalım
  const trLocaleManual = {
    code: 'tr',
    week: {
      dow: 1, // Pazartesi haftanın ilk günü
      doy: 7  // Yılın ilk haftasının oluşması için gereken minimum gün sayısı
    },
    buttonText: {
      prev: 'Önceki',
      next: 'Sonraki',
      today: 'Bugün',
      month: 'Ay',
      week: 'Hafta',
      day: 'Gün',
      list: 'Liste'
    },
    weekText: 'Hf',
    allDayText: 'Tüm gün',
    moreLinkText: 'daha fazla',
    noEventsText: 'Gösterilecek etkinlik yok',
    weekdays: {
      shorthand: ['Paz', 'Pts', 'Sal', 'Çar', 'Per', 'Cum', 'Cts'],
      longhand: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']
    },
    months: {
      shorthand: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
      longhand: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
    }
  };

  // FullCalendar'ın yüklendiği veya güncellendiği zaman çalışacak fonksiyon
  useEffect(() => {
    const addCustomButton = () => {
      setTimeout(() => {
        const rightHeaderEl = document.querySelector('.fc-toolbar-chunk:last-child');
        
        if (rightHeaderEl) {
          // Butonları her zaman temizle ve yeniden oluştur
          const existingCalendarBtn = document.querySelector('#add-calendar-event-btn');
          const existingBulkBtn = document.querySelector('#add-bulk-event-btn');
          
          if (existingCalendarBtn) {
            existingCalendarBtn.remove();
          }
          
          if (existingBulkBtn) {
            existingBulkBtn.remove();
          }
          
          // Özel etkinlik butonu oluştur
          const calendarEventButton = document.createElement('button');
          calendarEventButton.id = 'add-calendar-event-btn';
          calendarEventButton.className = 'fc-button fc-button-primary';
          calendarEventButton.innerHTML = `<span style="margin-right: 4px;">+</span> ${t('events.actions.addSpecialEvent')}`;
          calendarEventButton.style.marginRight = '5px';
          
          // Özel etkinlik butonuna tıklama işlevi ekle
          calendarEventButton.addEventListener('click', () => {
            setIsCalendarEventModalOpen(true);
          });
          
          // Toplu etkinlik butonu oluştur
          const bulkEventButton = document.createElement('button');
          bulkEventButton.id = 'add-bulk-event-btn';
          bulkEventButton.className = 'fc-button fc-button-primary';
          bulkEventButton.innerHTML = `<span style="margin-right: 4px;">+</span> ${t('events.actions.addBulkEvent')}`;
          bulkEventButton.style.marginRight = '5px';
          bulkEventButton.style.backgroundColor = '#FF9800'; // Turuncu arka plan
          bulkEventButton.style.borderColor = '#FF9800';
          
          // Toplu etkinlik butonuna tıklama işlevi ekle
          bulkEventButton.addEventListener('click', () => {
            // Toplu etkinlik modalını aç
            setIsBulkEventModalOpen(true);
          });
          
          // Butonları header'ın başına ekle
          const firstButton = rightHeaderEl.firstChild;
          if (firstButton) {
            rightHeaderEl.insertBefore(calendarEventButton, firstButton);
            rightHeaderEl.insertBefore(bulkEventButton, calendarEventButton);
          } else {
            rightHeaderEl.appendChild(bulkEventButton);
            rightHeaderEl.appendChild(calendarEventButton);
          }
        }
      }, 300); // DOM'un yüklenmesi için yeterli gecikme
    };

    // İlk yüklemede ve takvim görünümü değiştiğinde butonu ekle
    addCustomButton();

    // Pencere boyutu değiştiğinde de butonu ekle (responsive düzen değişebilir)
    window.addEventListener('resize', addCustomButton);
    
    // Sayfadaki içerik değişikliklerini gözlemlemek için MutationObserver ekle
    const observer = new MutationObserver((mutations) => {
      // DOM değişikliklerinde butonları kontrol et ve gerekiyorsa ekle
      addCustomButton();
    });
    
    // Gözlemlenecek elementin ve ayarların belirlenmesi
    const calendarElement = document.querySelector('.fc');
    if (calendarElement) {
      observer.observe(calendarElement, { 
        childList: true, 
        subtree: true 
      });
    }
    
    // Özel olaylara dinleyici ekle
    const handleOpenCalendarEventModal = () => {
      setIsCalendarEventModalOpen(true);
    };
    
    const handleOpenBulkEventModal = () => {
      setIsBulkEventModalOpen(true);
    };
    
    // Olayları dinle
    document.addEventListener('open-calendar-event-modal', handleOpenCalendarEventModal);
    document.addEventListener('open-bulk-event-modal', handleOpenBulkEventModal);

    return () => {
      window.removeEventListener('resize', addCustomButton);
      observer.disconnect();
      
      // Olay dinleyicilerini temizle
      document.removeEventListener('open-calendar-event-modal', handleOpenCalendarEventModal);
      document.removeEventListener('open-bulk-event-modal', handleOpenBulkEventModal);
    };
  }, [t]);
  
  // Takvim olayları yüklendiğinde veya güncellendiğinde filtrelemeleri uygula
  useEffect(() => {
    // calendarEvents değiştiğinde, yalnızca takvim hazır olduğunda filtreleme yap
    if (calendarRef.current && calendarEvents.length > 0) {
      // Performans için timeout ekleyerek asenkron çalıştır
      const timeoutId = setTimeout(() => {
        filterEventsByActiveCalendarEvents(calendarEvents);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [calendarEvents]);

  // Etkinlikleri Firebase'den yükleme
  useEffect(() => {
    let isActive = true; // Bileşen aktif mi kontrolü için bayrak
    
    const loadEvents = async () => {
      try {
        // Giriş yapmış kullanıcıyı kontrol et
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user || !isActive) return;
        
        // Kullanıcı verilerini al ve şirket ID'sini bul
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData || !isActive) return;
        
        const companyId = userData.companyId || null;
        
        if (!companyId || !isActive) return;
        
        // Önce özel etkinlikleri yükle ki renk bilgileri elimizde olsun
        const calendarEventsData = await firebaseDataService.getEvents(companyId, 'calendarEvents');
        let specialEventColors: {[key: string]: string} = {};
        
        if (calendarEventsData) {
          // Özel etkinliklerin renklerini bir nesneye ekle
          Object.entries(calendarEventsData).forEach(([id, data]: [string, any]) => {
            specialEventColors[id] = data.color || '#1976d2'; // Varsayılan renk yoksa mavi kullan
          });
        }
        
        // Etkinlikleri yükle (TEK SEFERLİK)
        const eventsRef = ref(database, `companies/${companyId}/events`);
        const eventsSnapshot = await get(eventsRef);
        
        if (!isActive) return;
        
        if (!eventsSnapshot.exists()) {
          setEvents([]);
          return;
        }
        
        const eventsData = eventsSnapshot.val();
        
        try {
          const eventsList = Object.entries(eventsData).map(([id, data]: [string, any]) => {
            const start = new Date(data.start);
            const end = new Date(data.end);
            
            const eventColor = data.eventColor
              ? data.eventColor
              : (data.specialEventId && specialEventColors[data.specialEventId])
                  ? specialEventColors[data.specialEventId]
                  : '#1976d2';
            
            return {
              id,
              title: data.title,
              start,
              end,
              allDay: data.allDay,
              notes: data.notes,
              repeatYearly: data.repeatYearly,
              specialEventId: data.specialEventId,
              eventColor: data.eventColor,
              backgroundColor: eventColor, 
              borderColor: eventColor 
            };
          });
          
          // Tekrarlanan yıllık etkinlikleri ekle
          const today = new Date();
          const thisYear = today.getFullYear();
          
          const recurringEvents = eventsList
            .filter(event => event.repeatYearly)
            .map(event => {
              const originalStart = new Date(event.start);
              const originalEnd = new Date(event.end);
              
              const yearDiff = thisYear - originalStart.getFullYear();
              if (yearDiff <= 0) return null;
              
              const newStart = new Date(originalStart);
              newStart.setFullYear(thisYear);
              
              const newEnd = new Date(originalEnd);
              newEnd.setFullYear(thisYear);
              
              if (newStart < today) {
                newStart.setFullYear(thisYear + 1);
                newEnd.setFullYear(thisYear + 1);
              }
              
              return {
                id: `${event.id}-recurring-${thisYear}`,
                title: `🔄 ${event.title}`,
                start: newStart,
                end: newEnd,
                allDay: event.allDay,
                notes: event.notes,
                repeatYearly: event.repeatYearly,
                specialEventId: event.specialEventId,
                backgroundColor: '#8e44ad',
                borderColor: '#8e44ad'
              } as EventItem;
            })
            .filter((event): event is EventItem => event !== null);
          
          setEvents([...eventsList, ...recurringEvents]);
        } catch (e) {
          console.error('Error while processing event data:', e);
        }
      } catch (error) {
        console.error('Error while loading events:', error);
      }
    };
    
    loadEvents();
    
    return () => {
      isActive = false;
    };
  }, []);

  // Özel etkinlikleri Firebase'den yükleme (TEK SEFERLİK)
  useEffect(() => {
    let isActive = true;
    
    const loadCalendarEvents = async () => {
      try {
        if (!isActive) return;
        setLoadingCalendarEvents(true);
        
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user || !isActive) {
          setLoadingCalendarEvents(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData || !isActive) {
          setLoadingCalendarEvents(false);
          return;
        }
        
        const companyId = userData.companyId || null;
        
        if (!companyId || !isActive) {
          setLoadingCalendarEvents(false);
          return;
        }
        
        // Özel etkinlikleri yükle (TEK SEFERLİK)
        const calendarEventsRef = ref(database, `companies/${companyId}/events/calendarEvents`);
        const snapshot = await get(calendarEventsRef);
        
        if (!isActive) return;
        
        if (!snapshot.exists()) {
          setCalendarEvents([]);
          setLoadingCalendarEvents(false);
          return;
        }
        
        try {
          const eventsData = snapshot.val();
          const eventsList = Object.entries(eventsData).map(([id, data]: [string, any]) => ({
            id,
          title: data.title || t('events.messages.untitledEvent'),
            description: data.description || '',
            createdAt: data.createdAt || Date.now(),
            isChecked: data.isChecked !== undefined ? data.isChecked : true,
            color: data.color || '#1976d2'
          }));
          
          eventsList.sort((a, b) => b.createdAt - a.createdAt);
          
          if (isActive) {
            setCalendarEvents(eventsList);
            
            if (eventsList.length > 0) {
              filterEventsByActiveCalendarEvents(eventsList);
            }
            setLoadingCalendarEvents(false);
          }
        } catch (error) {
          console.error('Error while processing special event data:', error);
          if (isActive) setLoadingCalendarEvents(false);
        }
      } catch (error) {
        console.error('Error while loading special events:', error);
        if (isActive) setLoadingCalendarEvents(false);
      }
    };
    
    loadCalendarEvents();
    
    return () => {
      isActive = false;
    };
  }, []);

  // Özel etkinlik silme işlemi
  const handleDeleteCalendarEvent = useCallback((eventId: string) => {
    // UI'dan silinen özel etkinliği kaldır
    const updatedCalendarEvents = calendarEvents.filter(e => e.id !== eventId);
    setCalendarEvents(updatedCalendarEvents);
    
    // Bu etkinliğe ait tüm takvim görünümlerini güncelle
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const allEvents = calendarApi.getEvents();
      
      // Silinen özel etkinliğe bağlı olan etkinlikleri güncelle
      allEvents.forEach((event: EventApi) => {
        if (event.extendedProps.specialEventId === eventId) {
          // Bu etkinliklerin özel etkinlik bağlantısını kaldır
          event.setExtendedProp('specialEventId', null);
          event.setExtendedProp('eventColor', null);
          // Varsayılan rengi geri yükle
          event.setProp('backgroundColor', '#1976d2');
          event.setProp('borderColor', '#1976d2');
        }
      });
    }
    
    setSnackbarMessage(t('events.messages.specialEventDeleted'));
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  }, [calendarEvents, calendarRef]);

  // Özel etkinlik detaylarını görüntüle
  const handleCalendarEventClick = useCallback((event: CalendarEvent) => {
    setSelectedCalendarEvent(event);
    setIsCalendarEventDetailModalOpen(true);
  }, []);

  // Mini takvim bileşeni
  const renderMiniCalendar = () => {
    const dayNames = getDayNames(Boolean(isTr));
    const daysArray = getDaysArray();

    return (
      <>
        <Paper elevation={3} sx={{ p: 2, height: 'fit-content', mb: 2 }}>
          {/* Mini takvim başlık */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <IconButton size="small" onClick={handlePrevMonth}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="bold">
              {format(miniCalendarMonth, 'MMMM yyyy', { locale: dateFnsLocale })}
            </Typography>
            <IconButton size="small" onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          
          {/* Bugüne git butonu */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleGoToToday}
              sx={{ 
                fontSize: '0.75rem', 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <TodayIcon fontSize="small" sx={{ mr: 0.5 }} />
              {t('events.calendar.today')}
            </IconButton>
            <GuideButton guideKey="events.calendar" size="small" sx={{ ml: 1 }} />
          </Box>
          
          {/* Gün isimleri */}
          <Grid container sx={{ mb: 1 }}>
            {dayNames.map((day, index) => (
              <Grid item xs={12/7} key={index}>
                <Typography 
                  variant="caption" 
                  align="center" 
                  sx={{ display: 'block', fontWeight: 'bold' }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>
          
          {/* Günler */}
          <Grid container>
            {daysArray.map((day, index) => (
              <Grid item xs={12/7} key={index}>
                {day ? (
                  <Box 
                    sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '30px',
                      width: '30px',
                      margin: '0 auto',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      bgcolor: isSameDay(day, currentDate) ? 'primary.main' : 
                               isSameDay(day, today) ? 'primary.light' : 
                               'transparent',
                      color: isSameDay(day, currentDate) ? 'white' : 
                             isSameDay(day, today) ? 'primary.contrastText' : 
                             !isSameMonth(day, miniCalendarMonth) ? 'text.disabled' : 
                             'text.primary',
                      '&:hover': {
                        bgcolor: isSameDay(day, currentDate) ? 'primary.dark' : 'action.hover'
                      }
                    }}
                    onClick={() => handleMiniDateSelect(day)}
                  >
                    <Typography variant="caption">
                      {format(day, 'd')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ height: '30px' }} />
                )}
              </Grid>
            ))}
          </Grid>
          
          {/* Etkinlikler listesi eklenebilir */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('events.upcomingEvents.title')}
            </Typography>
            {events
              .filter(event => {
                const eventDate = new Date(event.start as Date);
                return eventDate >= today && eventDate <= addMonths(today, 1);
              })
              .slice(0, 3)
              .map((event, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    p: 1, 
                    mb: 0.5, 
                    borderLeft: '3px solid', 
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {format(new Date(event.start as Date), 'dd MMM', { locale: dateFnsLocale })}
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {event.title}
                  </Typography>
                </Box>
              ))}
            {events.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('events.upcomingEvents.empty')}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Özel Etkinlikler Bölümü */}
        <Paper elevation={3} sx={{ p: 2, height: 'fit-content' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {t('events.specialEvents.title')}
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 1 }} />
          
          <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
            {loadingCalendarEvents ? (
              <Typography align="center" sx={{ py: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                {t('events.common.loading')}
              </Typography>
            ) : calendarEvents.length === 0 ? (
              <Typography align="center" sx={{ py: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                {t('events.specialEvents.emptyLine1')}
                <br />
                {t('events.specialEvents.emptyLine2')}
              </Typography>
            ) : (
              calendarEvents.map((event) => (
                <ListItem
                  key={event.id}
                  dense
                  divider
                  sx={{ 
                    py: 0.5, 
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: event.isChecked ? 'rgba(0, 150, 136, 0.08)' : 'rgba(211, 47, 47, 0.08)',
                    '&:hover': { 
                      bgcolor: event.isChecked ? 'rgba(0, 150, 136, 0.12)' : 'rgba(211, 47, 47, 0.12)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Switch
                      edge="start"
                      checked={event.isChecked}
                      onChange={() => handleToggleCalendarEvent(event.id)}
                      size="small"
                      color={event.isChecked ? "primary" : "error"}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'medium',
                          color: event.isChecked ? 'text.primary' : 'text.secondary'
                        }}
                      >
                        {event.title}
                      </Typography>
                    } 
                  />
                  <IconButton 
                    edge="end" 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCalendarEventClick(event);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))
            )}
          </List>
        </Paper>
      </>
    );
  };

  // Takvim etkinliği kaydetme fonksiyonu
  const handleSaveCalendarEvent = (eventData: { title: string; description: string; id?: string; createdAt?: number }) => {
    console.log('Calendar event saved:', eventData);
    // İşlem başarılı olduğunda bildirim gösterilebilir veya diğer işlemler yapılabilir
    // Veritabanı kayıt işlemi artık modal component içinde gerçekleşiyor
  };

  // Modal kapatıldığında state'leri temizle
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedStart(null);
    setSelectedEnd(null);
    setSelectedAllDay(false);
    setSelectedDate(null);
    setSelectedEvent(null);
    setIsEditing(false);
  };

  // Etkinlik taşıma handler'ı
  const handleEventDrop = async (dropInfo: EventDropArg) => {
    const event = dropInfo.event;
    const eventId = event.id;
    
    try {
      // Kullanıcı bilgilerini al
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        dropInfo.revert(); // İşlemi geri al
        setSnackbarMessage(t('events.messages.userLoginNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        dropInfo.revert(); // İşlemi geri al
        setSnackbarMessage(t('events.messages.userInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      const companyId = userData.companyId;
      
      if (!companyId) {
        dropInfo.revert(); // İşlemi geri al
        setSnackbarMessage(t('events.messages.companyInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      // Tekrarlanan yıllık etkinlikler veya local etkinlikler için veritabanı güncellemesi yapma
      if (eventId.includes('-recurring-') || eventId.startsWith('event-')) {
        setSnackbarMessage(t('events.messages.eventTypeCannotBeMoved'));
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        dropInfo.revert(); // İşlemi geri al
        return;
      }
      
      // Veritabanında güncelleme yap
      const eventRef = ref(database, `companies/${companyId}/events/${eventId}`);
      const eventSnapshot = await get(eventRef);
      
      if (!eventSnapshot.exists()) {
        dropInfo.revert(); // İşlemi geri al
        setSnackbarMessage(t('events.messages.eventNotFoundInDatabase'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      // Mevcut veriyi al ve sadece tarihleri güncelle
      const existingData = eventSnapshot.val();
      const updatedData = {
        ...existingData,
        start: event.start?.toISOString() || existingData.start,
        end: event.end?.toISOString() || existingData.end,
        allDay: event.allDay,
        updatedAt: Date.now()
      };
      
      // Veritabanını güncelle
      await update(eventRef, updatedData);
      
      // Yerel state'i güncelle
      const updatedEvents = events.map(e => 
        e.id === eventId ? {
          ...e,
          start: event.start || e.start,
          end: event.end || e.end,
          allDay: event.allDay
        } : e
      );
      setEvents(updatedEvents);
      
      // Başarı mesajı göster
      setSnackbarMessage(t('events.messages.eventMoved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error while moving event:', error);
      dropInfo.revert(); // İşlemi geri al
      setSnackbarMessage(t('events.messages.eventMoveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Toplu etkinlik kaydetme fonksiyonu
  const handleSaveBulkEvents = async (eventsData: EventFormItem[]): Promise<string[]> => {
    try {
      // Kullanıcı bilgilerini al
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        setSnackbarMessage(t('events.messages.userLoginNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return []; // Boş dizi döndür
      }
      
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        setSnackbarMessage(t('events.messages.userInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return []; // Boş dizi döndür
      }
      
      const companyId = userData.companyId;
      
      if (!companyId) {
        setSnackbarMessage(t('events.messages.companyInfoNotFound'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return []; // Boş dizi döndür
      }
      
      // Eklenen etkinliklerin ID'lerini tutan dizi
      const addedEventIds: string[] = [];
      
      // Tüm etkinlikleri veritabanına kaydet
      for (const eventData of eventsData) {
        const eventsRef = ref(database, `companies/${companyId}/events`);
        const newEventRef = push(eventsRef);
        const eventId = newEventRef.key as string;
        
        if (!eventId) {
          console.error('Event ID could not be created.');
          continue;
        }
        
        // Etkinlik verilerini hazırla
        const eventDataForDB = {
          ...eventData,
          id: eventId,
          createdAt: Date.now(),
          createdBy: user.uid,
          updatedAt: Date.now(),
          start: eventData.start.toISOString(),
          end: eventData.end.toISOString()
        };
        
        // Veritabanına kaydet
        await set(newEventRef, eventDataForDB);
        
        // UI'a ekle
        setEvents(prev => [...prev, {
          ...eventData,
          id: eventId,
          backgroundColor: eventData.eventColor || '#1976d2',
          borderColor: eventData.eventColor || '#1976d2'
        }]);
        
        // Eklenen etkinlik ID'sini diziye ekle
        addedEventIds.push(eventId);
      }
      
      // Başarılı mesajı
      const eventCount = eventsData.length;
      setSnackbarMessage(t('events.messages.bulkCreated', { count: eventCount }));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      return addedEventIds;
    } catch (error) {
      console.error('Error while saving bulk events:', error);
      setSnackbarMessage(t('events.messages.bulkSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return []; // Hata durumunda boş dizi döndür
    }
  };

  // Takvim görünümü değiştiğinde tetiklenir
  const handleViewChange = useCallback((viewName: string) => {
    // Günlük görünümden çıkılıyorsa son tarihi kaydet
    if (calendarView === 'timeGridDay' || calendarView === 'dayGridMonth') {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        setLastDayViewDate(calendarApi.getDate());
      }
    }
    
    // Ajanda görünümü kontrolü
    if (viewName === 'agenda') {
      setShowAgendaView(true);
    } else {
      setShowAgendaView(false);
    }
    
    setCalendarView(viewName);
    
    // Yıl görünümünden gün görünümüne geçerken son bilinen tarihe git
    if (viewName === 'timeGridDay' && lastDayViewDate) {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.gotoDate(lastDayViewDate);
      }
    }
  }, [calendarView, lastDayViewDate]);
  
  // Ajanda'dan takvime geri dönme
  const handleBackToCalendar = useCallback(() => {
    handleViewChange('dayGridMonth');
  }, [handleViewChange]);
  
  // Ajanda notlarını Firebase'den yükle
  const loadAgendaNotes = useCallback(async (date: Date) => {
    try {
      setLoadingAgendaNotes(true);
      
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        setLoadingAgendaNotes(false);
        return;
      }
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        setLoadingAgendaNotes(false);
        return;
      }
      
      const companyId = userData.companyId || null;
      
      if (!companyId) {
        setLoadingAgendaNotes(false);
        return;
      }
      
      // Seçili tarih için ajanda notlarını al
      const formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const notesRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}`);
      const snapshot = await get(notesRef);
      
      if (!snapshot.exists()) {
        setAgendaNotes([]);
      } else {
        const notesData = snapshot.val();
        const notesList = Object.entries(notesData).map(([id, data]: [string, any]) => ({
          id,
          date: formattedDate,
          content: data.content || '',
          createdAt: data.createdAt || Date.now(),
          updatedAt: data.updatedAt || Date.now()
        }));
        
        // Oluşturma tarihine göre sırala
        notesList.sort((a, b) => b.createdAt - a.createdAt);
        
        setAgendaNotes(notesList);
      }
    } catch (error) {
      console.error('Error while loading agenda notes:', error);
    } finally {
      setLoadingAgendaNotes(false);
    }
  }, []);

  // Tarih değiştiğinde ajanda notlarını güncelle
  const handleAgendaDateChange = useCallback((date: Date) => {
    setSelectedAgendaDate(date);
    loadAgendaNotes(date);
  }, [loadAgendaNotes]);

  // Yeni not ekle
  const handleAddNote = useCallback(async () => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Yeni bir not oluştur
      const formattedDate = selectedAgendaDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const notesRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}`);
      const newNoteRef = push(notesRef);
      
      const newNote = {
        content: '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: user.uid
      };
      
      // Veritabanına kaydet
      await set(newNoteRef, newNote);
      
      // UI'ı güncelle
      const noteId = newNoteRef.key;
      if (noteId) {
        setAgendaNotes([
          {
            id: noteId,
            date: formattedDate,
            content: '',
            createdAt: newNote.createdAt,
            updatedAt: newNote.updatedAt
          },
          ...agendaNotes
        ]);
        
        // Yeni not düzenleme modunda başlasın
        setEditingNote(noteId);
        setNoteContent('');
      }
    } catch (error) {
      console.error('Error while adding note:', error);
      setSnackbarMessage(t('events.messages.noteAddError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedAgendaDate, agendaNotes]);

  // Not düzenlemeye başla
  const handleEditNote = useCallback((note: AgendaNote) => {
    setEditingNote(note.id);
    setNoteContent(note.content);
  }, []);

  // Not içeriğini güncelle
  const handleNoteContentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNoteContent(e.target.value);
  }, []);

  // Not kaydetme
  const handleSaveNote = useCallback(async (noteId: string) => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Notu güncelle
      const formattedDate = selectedAgendaDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const noteRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}/${noteId}`);
      
      const updatedNoteData = {
        content: noteContent,
        updatedAt: Date.now()
      };
      
      // Veritabanını güncelle
      await update(noteRef, updatedNoteData);
      
      // UI'ı güncelle
      setAgendaNotes(agendaNotes.map(note => 
        note.id === noteId 
          ? { ...note, content: noteContent, updatedAt: updatedNoteData.updatedAt }
          : note
      ));
      
      // Düzenleme modunu kapat
      setEditingNote(null);
      
      // Başarı mesajı göster
      setSnackbarMessage(t('events.messages.noteSaved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error while saving note:', error);
      setSnackbarMessage(t('events.messages.noteSaveError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedAgendaDate, noteContent, agendaNotes]);

  // Not silme
  const handleDeleteNote = useCallback(async (noteId: string) => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Notu sil
      const formattedDate = selectedAgendaDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const noteRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}/${noteId}`);
      
      // Veritabanından sil
      await set(noteRef, null);
      
      // UI'dan sil
      setAgendaNotes(agendaNotes.filter(note => note.id !== noteId));
      
      // Düzenleme modu aktifse kapat
      if (editingNote === noteId) {
        setEditingNote(null);
      }
      
      // Başarı mesajı göster
      setSnackbarMessage(t('events.messages.noteDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error while deleting note:', error);
      setSnackbarMessage(t('events.messages.noteDeleteError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedAgendaDate, agendaNotes, editingNote]);

  // Ajanda görünümü
  const renderAgendaView = useCallback(() => {
    const formattedDate = format(selectedAgendaDate, 'dd MMMM yyyy', { locale: dateFnsLocale });
    
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">{t('events.agenda.title', { date: formattedDate })}</Typography>
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<EditIcon />}
              onClick={handleAddNote}
            >
              Yeni Not Ekle
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex' }}>
          {/* Sol taraf - Mini takvim */}
          <Box sx={{ width: 300, mr: 3 }}>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <IconButton size="small" onClick={handlePrevMonth}>
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                  {format(miniCalendarMonth, 'MMMM yyyy', { locale: dateFnsLocale })}
                </Typography>
                <IconButton size="small" onClick={handleNextMonth}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              
              <Grid container sx={{ mb: 1 }}>
                {getDayNames(Boolean(isTr)).map((day, index) => (
                  <Grid item xs={12/7} key={index}>
                    <Typography 
                      variant="caption" 
                      align="center" 
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      {day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              
              <Grid container>
                {getDaysArray().map((day, index) => (
                  <Grid item xs={12/7} key={index}>
                    {day ? (
                      <Box 
                        sx={{ 
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '30px',
                          width: '30px',
                          margin: '0 auto',
                          cursor: 'pointer',
                          borderRadius: '50%',
                          bgcolor: isSameDay(day, selectedAgendaDate) ? 'primary.main' : 
                                  isSameDay(day, today) ? 'primary.light' : 
                                  'transparent',
                          color: isSameDay(day, selectedAgendaDate) ? 'white' : 
                                isSameDay(day, today) ? 'primary.contrastText' : 
                                !isSameMonth(day, miniCalendarMonth) ? 'text.disabled' : 
                                'text.primary',
                          '&:hover': {
                            bgcolor: isSameDay(day, selectedAgendaDate) ? 'primary.dark' : 'action.hover'
                          }
                        }}
                        onClick={() => handleAgendaDateChange(day)}
                      >
                        <Typography variant="caption">
                          {format(day, 'd')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ height: '30px' }} />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
          
          {/* Sağ taraf - Notlar */}
          <Box sx={{ flex: 1 }}>
            {loadingAgendaNotes ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : agendaNotes.length === 0 ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('events.agenda.emptyLine1')}
                  <br />
                  {t('events.agenda.emptyLine2')}
                </Typography>
              </Paper>
            ) : (
              <Box>
                {agendaNotes.map(note => (
                  <Card key={note.id} sx={{ mb: 2, position: 'relative' }}>
                    <CardContent>
                      {editingNote === note.id ? (
                        <>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={20}
                            value={noteContent}
                            onChange={handleNoteContentChange}
                            placeholder={t('events.agenda.notePlaceholder')}
                            variant="outlined"
                            autoFocus
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button 
                              onClick={() => setEditingNote(null)} 
                              sx={{ mr: 1 }}
                            >
                              {t('events.eventForm.actions.cancel')}
                            </Button>
                            <Button 
                              variant="contained" 
                              color="primary"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveNote(note.id)}
                            >
                              {t('events.eventForm.actions.save')}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ 
                            whiteSpace: 'pre-wrap',
                            minHeight: '50px'
                          }}>
                            {note.content || <i>{t('events.agenda.emptyNote')}</i>}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('events.agenda.lastUpdated', { date: format(new Date(note.updatedAt), 'dd MMM yyyy HH:mm', { locale: dateFnsLocale }) })}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            display: 'flex'
                          }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditNote(note)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [
    selectedAgendaDate, 
    miniCalendarMonth, 
    agendaNotes, 
    loadingAgendaNotes, 
    editingNote, 
    noteContent,
    handleAgendaDateChange,
    handleAddNote,
    handleEditNote,
    handleNoteContentChange,
    handleSaveNote,
    handleDeleteNote
  ]);

  // Yıl görünümündeki bir tarihe tıklandığında
  const handleYearDateClick = useCallback((date: Date) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.gotoDate(date);
      handleViewChange('dayGridMonth');
    }
  }, [handleViewChange]);

  return (
    <Box sx={{ 
      height: 'calc(100vh - 80px)', // Sayfa yüksekliğini ayarla
      p: 2,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Grid container spacing={2} sx={{ height: '100%', flexGrow: 1 }}>
        {/* Sol taraf - Mini Takvim ve Özel Etkinlikler (Ajanda görünümünde gizli) */}
        {!showAgendaView && (
          <Grid item xs={12} md={3} lg={2} sx={{ 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column'
          }}>
            <Box sx={{ 
              height: '100%', 
              overflowY: 'auto', // Sol taraf için dikey kaydırma çubuğu
              pr: 1, // Kaydırma çubuğu için sağ tarafta biraz boşluk
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
            }}>
              {renderMiniCalendar()}
            </Box>
          </Grid>
        )}
        
        {/* Sağ taraf - Ana Takvim veya Ajanda */}
        <Grid item xs={12} md={showAgendaView ? 12 : 9} lg={showAgendaView ? 12 : 10} sx={{ 
          height: '100%',
        }}>
          <Box sx={{ 
            height: '100%',
            overflowY: 'auto', // Ana takvim için dikey kaydırma çubuğu
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
            '& .fc .fc-toolbar-title': { fontSize: '1.5rem' },
            '& .fc-event': { 
              cursor: 'pointer'
            },
            '& .fc-event-title': {
              color: 'white !important',
              fontWeight: 'bold',
              padding: '2px 4px'
            },
            '& .fc-event-time': {
              color: 'white !important',
              fontWeight: 'bold'
            },
            '& .fc-daygrid-event': {
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              marginBottom: '2px'
            },
            '& .fc-h-event .fc-event-main': {
              padding: '2px 4px'
            },
            '& .fc-daygrid-event-dot': {
            },
            '& .fc-list-event-dot': {
            },
            '& .fc-list-event:hover td': {
              backgroundColor: 'rgba(25, 118, 210, 0.1) !important'
            },
            '& .fc-list-event-title, & .fc-list-event-time': {
            }
          }}>
            {showAgendaView ? (
              <Agenda 
                onBackToCalendar={handleBackToCalendar}
                initialDate={calendarRef.current?.getApi().getDate()}
              />
            ) : (
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, multiMonthPlugin]}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay,multiMonthYear,listWeek,agenda'
                }}
                customButtons={{
                  agenda: {
                    text: t('events.calendar.agenda'),
                    click: () => {
                      handleViewChange('agenda');
                    }
                  }
                }}
                initialView="dayGridMonth"
                editable={true}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={false}
                weekends={true}
                events={events}
                dateClick={(arg) => {
                  if (calendarView === 'multiMonthYear') {
                    handleYearDateClick(arg.date);
                  } else {
                    handleDateClick(arg);
                  }
                }}
                eventClick={handleEventClick}
                select={handleDateSelect}
                eventDrop={handleEventDrop}
                locale={isTr ? 'tr' : 'en'}
                locales={[trLocaleManual]}
                height="auto"
                firstDay={1}
                dayCellClassNames={(arg) => {
                  const dayEvents = events.filter(event => {
                    const eventStart = new Date(event.start);
                    return isSameDay(eventStart, arg.date);
                  });
                  
                  if (dayEvents.length > 5) {
                    return 'many-events-xl';
                  } else if (dayEvents.length > 3) {
                    return 'many-events-lg';
                  } else if (dayEvents.length > 1) {
                    return 'many-events-md';
                  }
                  return '';
                }}
                dayCellDidMount={(arg) => {
                  // Yıl görünümünde hücre boyutlarına dokunmayalım
                  if (calendarView === 'multiMonthYear') return;
                  
                  arg.el.style.minHeight = '120px';
                  
                  const dayEvents = events.filter(event => {
                    const eventStart = new Date(event.start);
                    return isSameDay(eventStart, arg.date);
                  });
                  
                  if (dayEvents.length > 5) {
                    arg.el.style.minHeight = '200px';
                  } else if (dayEvents.length > 3) {
                    arg.el.style.minHeight = '160px';
                  }
                }}
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: false,
                  hour12: false
                }}
                eventContent={(eventInfo) => {
                  const customColor = eventInfo.event.extendedProps.eventColor || 
                                    eventInfo.event.backgroundColor || 
                                    '#1976d2';
                  
                  const style = {
                    backgroundColor: customColor,
                    borderColor: customColor,
                    color: 'white',
                    width: '100%'
                  };
                  
                  return (
                    <div style={style} className="fc-event-main-custom">
                      {eventInfo.timeText && (
                        <div className="fc-event-time-custom" style={{fontWeight: 'bold', padding: '2px 4px'}}>
                          {eventInfo.timeText}
                        </div>
                      )}
                      <div className="fc-event-title-custom" style={{fontWeight: 'bold', padding: '2px 4px'}}>
                        {eventInfo.event.title}
                      </div>
                    </div>
                  );
                }}
                buttonText={{
                  today: t('events.calendar.today'),
                  month: t('events.calendar.month'),
                  week: t('events.calendar.week'),
                  day: t('events.calendar.day'),
                  list: t('events.calendar.list'),
                  year: t('events.calendar.year')
                }}
                viewDidMount={(arg) => {
                  handleViewChange(arg.view.type);
                }}
                allDayText={t('events.calendar.allDay')}
                moreLinkText={t('events.calendar.more')}
                noEventsText={t('events.calendar.noEvents')}
                lazyFetching={true}
                stickyHeaderDates={true}
                rerenderDelay={10}
                contentHeight="auto"
                // Yıl görünümü için ekstra stil
                views={{
                  multiMonthYear: {
                    titleFormat: { year: 'numeric' },
                    multiMonthMinWidth: 200, // Minimum genişlik
                    multiMonthMaxColumns: 4,  // Maximum satırda ay sayısı
                  }
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Etkinlik düzenleme modal'ı */}
      <EventFormModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveEvent}
        onDelete={isEditing ? handleDeleteEvent : undefined}
        initialData={
          isEditing && selectedEvent 
            ? { 
                title: selectedEvent.title, 
                start: new Date(selectedEvent.start as Date), 
                end: selectedEvent.end ? new Date(selectedEvent.end as Date) : undefined,
                allDay: selectedEvent.allDay,
                notes: selectedEvent.notes,
                repeatYearly: selectedEvent.repeatYearly,
                specialEventId: selectedEvent.specialEventId
              } 
            : selectedDate 
              ? { 
                  start: selectedDate,
                  end: new Date(new Date(selectedDate).getTime() + 60 * 60 * 1000)
                } 
              : selectedStart && selectedEnd // Tarih aralığı seçimi
                ? {
                    start: selectedStart,
                    end: selectedEnd,
                    allDay: selectedAllDay
                  }
                : undefined
        }
        isEditing={isEditing}
      />

      {/* Takvim etkinliği ekleme modal'ı */}
      <CalendarEventModal
        open={isCalendarEventModalOpen}
        onClose={() => setIsCalendarEventModalOpen(false)}
        onSave={handleSaveCalendarEvent}
      />

      {/* Toplu etkinlik ekleme modal'ı */}
      <BulkEventModal
        open={isBulkEventModalOpen}
        onClose={() => setIsBulkEventModalOpen(false)}
        onSave={handleSaveBulkEvents}
        calendarEvents={calendarEvents}
      />
      
      {/* Özel etkinlik detay modalı */}
      <CalendarEventDetailModal
        open={isCalendarEventDetailModalOpen}
        onClose={() => setIsCalendarEventDetailModalOpen(false)}
        calendarEvent={selectedCalendarEvent}
        onDelete={handleDeleteCalendarEvent}
      />

      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events; 